import React from 'react';

import { narrativeStructure } from '../data/narrativeStructure';
import { NarrativeState } from '../types/narrative';
import { shouldShowVariantTable } from '../utils/variantTableHelpers';

interface PathsToNodeProps {
  nodeId: string;
  narrativeState: NarrativeState;
}

interface PathChoice {
  fromNodeId: string;
  fromNodeAlias: string;
  choiceIndex: number;
  choiceText: string;
}

export const PathsToNode = React.memo(function PathsToNode({
  nodeId,
  narrativeState,
}: PathsToNodeProps) {
  // Don't show for first node or nodes that already have variant tables
  if (nodeId === 'S1_Opening_Hook' || shouldShowVariantTable(nodeId)) {
    return null;
  }

  // Get the node from state or structure
  const node = narrativeState.nodes[nodeId] || narrativeStructure[nodeId];
  if (!node || !node.parentIds || node.parentIds.length === 0) {
    return null;
  }

  // Collect all possible paths to this node
  const pathsToNode: PathChoice[] = [];

  node.parentIds.forEach((parentId: string) => {
    const parentNode = narrativeState.nodes[parentId] || narrativeStructure[parentId];
    if (!parentNode || !parentNode.choices) return;

    parentNode.choices.forEach((choice, index) => {
      if (choice.goto === nodeId) {
        // Special handling for world-variant nodes - only show relevant choices
        if (nodeId.startsWith('S3A_') || nodeId.startsWith('S3B_')) {
          const isGoodWorldNode = nodeId.startsWith('S3A_');
          const isBadWorldNode = nodeId.startsWith('S3B_');

          // For S2_Return_To_A_Plot_Player_Led choices, filter based on destination
          if (parentId === 'S2_Return_To_A_Plot_Player_Led') {
            // Choices 0 and 3 lead to good world, choices 1 and 2 lead to bad world
            const choiceLeadsToGoodWorld = index === 0 || index === 3;
            const choiceLeadsToBadWorld = index === 1 || index === 2;

            if (isGoodWorldNode && !choiceLeadsToGoodWorld) {
              return; // Skip choices that don't lead to good world
            }
            if (isBadWorldNode && !choiceLeadsToBadWorld) {
              return; // Skip choices that don't lead to bad world
            }
          }
        }

        pathsToNode.push({
          fromNodeId: parentId,
          fromNodeAlias: parentNode.idAlias || parentNode.id.replace(/_/g, ' '),
          choiceIndex: index,
          choiceText: choice.text || `Choice ${index + 1}`,
        });
      }
    });
  });

  if (pathsToNode.length === 0) {
    return null;
  }

  return (
    <div className="mb-4 text-xs">
      {/* Glassy card container matching VariantTable style */}
      <div className="bg-black bg-opacity-30 backdrop-blur-sm border border-gray-700 rounded-lg p-4">
        {/* Header text */}
        <p className="text-gray-300 mb-2">
          {pathsToNode.length === 1
            ? 'This choice is the only one possible to lead to this node:'
            : 'The following choices could have directed the player to this node:'}
        </p>

        {/* Top horizontal line */}
        <hr className="border-gray-600 mb-3" />

        {/* Paths list */}
        <div className="space-y-2">
          {pathsToNode.map((path) => (
            <div key={`${path.fromNodeId}-${path.choiceIndex}`} className="text-gray-400">
              <span className="font-medium text-gray-300">{path.fromNodeAlias}</span>
              <span className="text-gray-500"> → </span>
              <span className="text-white font-bold">{path.choiceText.toUpperCase()}</span>
            </div>
          ))}
        </div>

        {/* Bottom horizontal line */}
        <hr className="border-gray-600 mt-3" />
      </div>
    </div>
  );
});
